import { assertNever, getUnsafeChainIdToDomain, NormalizedNumber, SparkDomain } from '@sparkdotfi/common-universal'
import { ExternalLinkIcon } from 'lucide-react'

import { getChainConfigEntry } from '@/config/chain'
import { SUPPORTED_CHAIN_IDS } from '@/config/chain/constants'
import { PointsType } from '@/features/points/types'
import { InternalLink } from '@/routing/types'
import { Button, ButtonIcon } from '@/ui/atoms/button/Button'
import { ExternalLinkButton, InternalLinkButton } from '@/ui/atoms/link-button/LinkButton'
import { links } from '@/ui/constants/links'

export interface ActionButtonProps {
  userChainId: number
  type: PointsType
  pointsAmount: NormalizedNumber
  campaignActive: boolean
  isGuestMode: boolean
  onInviteClick: () => void
  onBuyClick: () => void
  className?: string
}

export function ActionButton({
  userChainId,
  type,
  pointsAmount,
  campaignActive,
  isGuestMode,
  onBuyClick,
  onInviteClick,
  className,
}: ActionButtonProps) {
  const variant = pointsAmount.gt(NormalizedNumber.ZERO) ? 'secondary' : 'primary'

  switch (type) {
    case 'pendle':
      return isGuestMode ? (
        <Button variant={variant} size="s" className={className} disabled>
          Buy
          <ButtonIcon icon={ExternalLinkIcon} />
        </Button>
      ) : (
        <ExternalLinkButton
          to={links.pendleWebsite}
          variant={variant}
          size="s"
          className={className}
          onClick={(e) => {
            e.stopPropagation()
            onBuyClick()
          }}
        >
          Buy
          <ButtonIcon icon={ExternalLinkIcon} />
        </ExternalLinkButton>
      )
    case 'referral':
      return (
        <Button
          variant={variant}
          size="s"
          className={className}
          disabled={isGuestMode}
          onClick={(e) => {
            e.stopPropagation()
            onInviteClick()
          }}
        >
          Invite
        </Button>
      )

    case 'staking':
      return isGuestMode ? (
        <Button variant={variant} size="s" className={className} disabled>
          Stake
        </Button>
      ) : (
        <InternalLinkButton to="/spk/staking" variant={variant} size="s" className={className}>
          Stake
        </InternalLinkButton>
      )
    case 'hold-susdc':
    case 'hold-susds':
    case 'hold-spusdc':
    case 'hold-spusdt':
    case 'hold-speth':
      return (
        <DepositToSavingsButton
          userChainId={userChainId}
          variant={variant}
          className={className}
          isGuestMode={isGuestMode}
          type={type}
          campaignActive={campaignActive}
        />
      )
    case 'governance':
      return isGuestMode ? (
        <Button variant={variant} size="s" className={className} disabled>
          Delegate
        </Button>
      ) : (
        <InternalLinkButton variant={variant} size="s" className={className} to="/spk/governance">
          Delegate
        </InternalLinkButton>
      )
    case 'loyalty':
      return isGuestMode ? (
        <Button variant={variant} size="s" className={className} disabled>
          Learn
          <ButtonIcon icon={ExternalLinkIcon} />
        </Button>
      ) : (
        <ExternalLinkButton
          to={links.docs.points}
          variant={variant}
          size="s"
          className={className}
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          Learn
          <ButtonIcon icon={ExternalLinkIcon} />
        </ExternalLinkButton>
      )
    default:
      assertNever(type)
  }
}

interface DepositToSavingsButtonProps {
  userChainId: number
  variant: 'primary' | 'secondary'
  className?: string
  isGuestMode: boolean
  campaignActive: boolean
  type: Extract<PointsType, 'hold-susdc' | 'hold-susds' | 'hold-spusdc' | 'hold-spusdt' | 'hold-speth'>
}

function DepositToSavingsButton({
  userChainId,
  variant,
  className,
  isGuestMode,
  type,
  campaignActive,
}: DepositToSavingsButtonProps) {
  const savingsTokenSymbol = (() => {
    switch (type) {
      case 'hold-susdc':
        return 'sUSDC'
      case 'hold-susds':
        return 'sUSDS'
      case 'hold-spusdc':
        return 'spUSDC'
      case 'hold-spusdt':
        return 'spUSDT'
      case 'hold-speth':
        return 'spETH'
    }
  })()

  const savingsTokenIsOnUserChain =
    SUPPORTED_CHAIN_IDS.includes(userChainId) &&
    getChainConfigEntry(userChainId).savings?.accounts?.some((account) => account.savingsToken === savingsTokenSymbol)
  const linkDomain: SparkDomain = savingsTokenIsOnUserChain ? getUnsafeChainIdToDomain(userChainId) : 'mainnet'
  const link = InternalLink({
    to: '/savings/$domain/$savingsToken',
    params: { domain: linkDomain, savingsToken: savingsTokenSymbol.toLowerCase() },
  })

  return isGuestMode || !campaignActive ? (
    <Button variant={variant} size="s" className={className} disabled>
      Deposit
    </Button>
  ) : (
    <InternalLinkButton {...link} variant={variant} size="s" className={className}>
      Deposit
    </InternalLinkButton>
  )
}
